import { React, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import NewTitleInput from '../../../Component/NoticeComponent/NewTitleInput';
import NewTextArea from '../../../Component/NoticeComponent/NewTextArea';
import NoticePictureInput from './NoticePictureInput';
import AiAssistant from '../../Share/Normal/AiAssistant';
import CustomScrollbar from '../../Share/Normal/CustomScrollbar';

import { ReactComponent as ExitButtonSvg } from '../../../image/ExitButton.svg';
import { ReactComponent as URLIconSvg } from '../../../image/Url.svg';
import { ReactComponent as PinIconSvg } from '../../../image/Pin.svg';

const NewNoticeModifyBox = forwardRef((props, ref) => {
  const { data } = props;

  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [value, setValue] = useState('');
  const [storeLogo, setStoreLogo] = useState('');

  const [urls, setUrls] = useState([]);

  const [isNeedCheckURL, setIsNeedCheckURL] = useState(false);
  const [isClickedFixed, setIsClickedFixed] = useState(false);

  const [initialPictures, setInitialPictures] = useState([]);

  const TitleRef = useRef();
  const PictureRef = useRef();
  const VideoScrollRef = useRef();
  const dataInitializedRef = useRef(false);

  useImperativeHandle(ref, () => ({
    getTitle: () => {
      return TitleRef.current.getValue();
    },
    getValue: () => {
      let fianlURL = '';

      for (let i = 0; i < urls.length; i++) {
        fianlURL += (urls[i] + ' ');
      }

      return value + '\n' + fianlURL;
    },
    getPicture: () => {
      return PictureRef.current.getPictures();
    },
    getIsFixed: () => {
      return isClickedFixed;
    }
  }))

  // 서버 데이터로 초기화 (1회) //
  useEffect(() => {
    if (dataInitializedRef.current) return;
    if (!data || !data.createdDate) return;

    let newContent = data.content || '';
    const extractedUrls = [];

    while (true) {
      const url = collectURL(newContent);
      if (url === '') break;
      newContent = newContent.replace(url, '');
      extractedUrls.push(url.replaceAll(',', ''));
    }

    setUrls(extractedUrls);
    setValue(newContent);

    if (TitleRef.current && TitleRef.current.setValue) {
      TitleRef.current.setValue(data.title || '');
    }

    setIsClickedFixed(!!data.isFixed);

    if (data.storeLogo) setStoreLogo(data.storeLogo);

    if (data.picture && data.picture !== '') {
      setInitialPictures(data.picture.split('<-@->'));
    }

    dataInitializedRef.current = true;
  }, [data])

  // 입력값 변경 시 URL 자동 추출 //
  useEffect(() => {
    if (!isNeedCheckURL) return;

    let newContent = value;
    let newUrls = urls.slice();

    while (true) {
      let url = collectURL(newContent);

      if (url === '') break;
      else {
        newContent = newContent.replace(url, '');
        newUrls.push(url.replaceAll(',', ''));
      }
    }

    setUrls(newUrls);
    setValue(newContent);
    setIsNeedCheckURL(false);
  }, [value, urls, isNeedCheckURL])

  // 비디오 영역 휠 → 가로 스크롤 //
  useEffect(() => {
    const el = VideoScrollRef.current;
    if (!el) return;

    function onWheel(e) {
      if (el.scrollWidth <= el.clientWidth) return;
      const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      if (delta === 0) return;
      e.preventDefault();
      el.scrollLeft += delta;
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [urls])

  // 붙여넣기나 스페이스바, 엔터키 감지시 URL 체크 //
  function onCheckURL() {
    setTimeout(() => {
      setIsNeedCheckURL(true);
    }, 100);
  }

  // 공지사항내에 링크 추출 //
  function collectURL(text) {
    let urlRegex = /(https?:\/\/[^ ]*)/;
    let url = text.match(urlRegex) === null ? '' : text.match(urlRegex)[1];

    return url;
  }

  // 링크 클릭시 //
  function onClickURL(url) {
    window.open(url, "_blank", "noreferrer");
  }

  // URL 삭제 클릭시 //
  function onClickDeleteURL(index) {
    let newURLs = urls.slice();

    newURLs.splice(index, 1);
    setUrls(newURLs);
  }

  // 유튜브링크인지 확인 //
  function decideYoutubeLink(url) {
    if (!url.includes('https://youtu.be/') && !url.includes('https://www.youtube.com/watch?v=')) {
      return false;
    }
    else if (url.includes('https://youtu.be/')
      && (url.split('https://youtu.be/')[0] !== '' || url.split('https://youtu.be/')[1] === '')) {
      return false;
    }
    else if (url.includes('https://www.youtube.com/watch?v=')
      && (url.split('https://www.youtube.com/watch?v=')[0] !== '' || url.split('https://www.youtube.com/watch?v=')[1] === '')) {
      return false;
    }
    else return true;
  }

  // 유튜브링크인지 확인후 Id 만 추출 //
  function decideYoutubeLinkVideoId(url) {
    if (url.includes('https://youtu.be/')) return url.replace('https://youtu.be/', '');
    else if (url.includes('https://www.youtube.com/watch?v=')) return url.replace('https://www.youtube.com/watch?v=', '')
  }

  // AI 어시스턴트 생성 컨텍스트 //
  function decideAiContext() {
    return { currentTitle: TitleRef.current?.getValue?.() ?? '', currentContent: value };
  }

  // AI 어시스턴트 결과 적용 //
  function onApplyAiResult(title, content) {
    TitleRef.current?.setValue(title);
    setValue(content);
  }

  return (
    <TopLevelWrapper>
      <ViewWrapper isDark={isDark}>
        <ComponentWrapper>
          <TitleWrapper>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('noticePage.titleLabel')}</Text>
            <FixingWrapper>
              <PinIcon />
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('noticePage.pinToTop')}</Text>
            </FixingWrapper>
          </TitleWrapper>
          <TitleInputWrapper>
            <NewTitleInput ref={TitleRef} storeLogo={storeLogo} placeholder={t('noticePage.titlePlaceholder')} />
            <SlideComponentWrappper onClick={() => setIsClickedFixed(prev => !prev)}>
              <SlideOutBox isClicked={isClickedFixed}>
                <SlideInBox isClicked={isClickedFixed} />
              </SlideOutBox>
            </SlideComponentWrappper>
          </TitleInputWrapper>
        </ComponentWrapper>
        <ContentWrapper>
          <ComponentWrapper>
            <Title>{t('noticePage.contentLabel')}</Title>
            <NewTextArea value={value} setValue={setValue} checkURL={onCheckURL} />
          </ComponentWrapper>
          {
            urls.some(url => !decideYoutubeLink(url)) &&
            <URLWrapper>
              {
                urls.map((data, index) => (
                  !decideYoutubeLink(data) &&
                  <URLTextBox key={'URLTextBox-' + index}>
                    <InnerURLTextBox onClick={() => onClickURL(data)}>
                      <URLIcon />
                      <TextWrapper>
                        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='pointer'>{data}</Text>
                      </TextWrapper>
                    </InnerURLTextBox>
                    <DeleteButton onClick={() => onClickDeleteURL(index)} margin='0px 0px 0px 0px' width={20} height={20} radius={20}>
                      <ExitIcon width={9} height={9} />
                    </DeleteButton>
                  </URLTextBox>
                ))
              }
            </URLWrapper>
          }
          {
            urls.some(url => decideYoutubeLink(url)) &&
            <VideoOuterWrapper $hasScrollbar={urls.filter(url => decideYoutubeLink(url)).length >= 2}>
              <VideoWrapper ref={VideoScrollRef}>
                {
                  urls.map((data, index) => (
                    decideYoutubeLink(data) &&
                    <VideoViewWrapper key={'VideoViewWrapper-' + index}>
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/` + decideYoutubeLinkVideoId(data)}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Embedded youtube" />
                      <VideoDeleteButtonWrapper onClick={() => onClickDeleteURL(index)} >
                        <DeleteButton margin='10px 10px 0px 0px' width={20} height={20} radius={20}>
                          <ExitIcon width={9} height={9} />
                        </DeleteButton>
                      </VideoDeleteButtonWrapper>
                    </VideoViewWrapper>
                  ))
                }
              </VideoWrapper>
              {
                urls.filter(url => decideYoutubeLink(url)).length >= 2 &&
                <ScrollbarWrapper>
                  <CustomScrollbar
                    direction='horizontal'
                    scrollRef={VideoScrollRef}
                    IsColorScrollBar={true}
                    isTrackThin={true}
                    showTrackAlways={false} />
                </ScrollbarWrapper>
              }
            </VideoOuterWrapper>
          }
        </ContentWrapper>
        <DivisionLine />
        <AiAssistantWrapper>
          <AiAssistant
            type='notice'
            context={decideAiContext}
            onApply={onApplyAiResult} />
        </AiAssistantWrapper>
      </ViewWrapper>
      <NoticePictureInput ref={PictureRef} initialPictures={initialPictures} />
    </TopLevelWrapper>
  )
})

export default NewNoticeModifyBox;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 1035px;

  gap: 20px;

  margin-top: 30px;
  margin-bottom: 96px;
  padding-bottom: 68px;
`;

const ViewWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 10px 0;

  box-sizing: border-box;
  width: 590px;

  gap: 20px;
`;

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  padding: 0px 20px;

  box-sizing: border-box;
  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  width: 100%;
  height: 26px;
`;

const FixingWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 3px;

  white-space: nowrap;

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const PinIcon = styled(PinIconSvg)`
  width: 16px;
  height: 16px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.text};
    transition: fill 0.3s ease-in-out;
  }
`;

const TitleInputWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const SlideComponentWrappper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 8px;

  transition: all 0.3s ease-in-out;
`;

const SlideOutBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  box-sizing: border-box;
  width: 70px;
  height: 32px;

  border: ${props => props.isClicked ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.line}`};
  border-radius: 9px;
  background-color: ${props => props.isClicked ? props.theme.primary : props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const SlideInBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-left: ${props => props.isClicked ? '43px' : '4px'};

  width: 22px;
  height: 22px;

  border-radius: 7px;
  background-color: ${props => props.isClicked ? props.theme.white : props.theme.line};

  transition: all 0.3s ease-in-out;
`;

const Title = styled.p`
  display: flex;
  flex-direction: row;

  height: 26px;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  margin: 0 0 0 0;

  color: ${props => props.theme.text};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 550px;

  gap: 20px;
`;

const DivisionLine = styled.div`
  margin: 10px 30px;

  width: 530px;
  height: 1px;

  background-color: ${props => props.theme.line};
`;

const AiAssistantWrapper = styled.div`
  width: 550px;

  padding: 0 20px;
`

const URLWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin: 0 20px;

  width: 550px;

  gap: 4px;
`;

const URLTextBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 15px;

  box-sizing: border-box;
  width: 100%;
  height: 52px;

  gap: 10px;

  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const InnerURLTextBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 1;
  min-width: 0;
  height: 22px;

  gap: 10px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: 22px;

  overflow: hidden;
`;

const VideoOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin: 0 20px;
  margin-bottom: ${props => props.$hasScrollbar ? '0' : '25px'};

  width: 550px;
`;

const VideoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;

  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ScrollbarWrapper = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const VideoViewWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-end;

  width: 300px;
  height: 169px;

  flex-shrink: 0;

  overflow: hidden;

  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  & iframe {
    display: block;
    border: none;
    background-color: ${props => props.theme.subBackground};
    pointer-events: none;
  }
`;

const VideoDeleteButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-end;

  width: 100%;
`;

const DeleteButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex-shrink: 0;

  margin: ${props => props.margin};

  width: ${props => props.width}px;
  height: ${props => props.height}px;

  border-radius: ${props => props.radius}px;
  background-color: ${props => props.theme.buttonFill3};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ExitIcon = styled(ExitButtonSvg)`
  width: ${props => props.width}px;
  height: ${props => props.height}px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.text};
  }

  & rect {
    fill: ${props => props.theme.text};
  }
`;

const URLIcon = styled(URLIconSvg)`
  width: 20px;
  height: 20px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.text};
    transition: fill 0.3s ease-in-out;
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.cursor};
    }
  }
`;
